import { cursorPlayback } from "./cursor_playback"

import type { AnimatedCursorImage, CursorImage } from "../extension/client.types"

// No path leaves that side to the browser's own cursor.
export type CursorPreviewImage = Pick<CursorImage, "offsetX" | "offsetY"> & { path: string | null }

export type AnimatedCursorPreviewImage = CursorPreviewImage &
  Pick<AnimatedCursorImage, "frames" | "animationSpeed" | "animationDirection">

// Only the cursor animates.
export type CursorPreviewPair = {
  cursor: CursorPreviewImage | AnimatedCursorPreviewImage
  pointer: CursorPreviewImage
}

const ACTIVE_CLASS = "cursor-preview-active"
const CURRENT_CLASS = "cursor-preview-current"
// 128px sources displayed as 32px cursors; hotspots are stored in 128-space.
const SCALE = 4

const cursorValue = (image: CursorPreviewImage, url: string, fallback: string) =>
  `image-set(url("${url}") ${SCALE}x) ${Math.floor(image.offsetX / SCALE)} ${Math.floor(image.offsetY / SCALE)}, ${fallback}`

/*
 * Swaps the page's cursor and pointer for a pack's pair. One preview at a time, page-wide:
 * starting one stops the current one. The marker is the element that started it, which carries
 * .cursor-preview-current for the preview-on: variant.
 * */
export class CursorPreview {
  static #current: { marker: Element; timer?: number } | null = null

  static start(marker: Element, { cursor, pointer }: CursorPreviewPair) {
    this.stop()
    const root = document.documentElement

    root.style.setProperty(
      "--cursor-preview-pointer",
      pointer.path ? cursorValue(pointer, pointer.path, "pointer") : "pointer",
    )
    root.classList.add(ACTIVE_CLASS)
    marker.classList.add(CURRENT_CLASS)
    this.#current = { marker }

    if ("frames" in cursor) {
      this.#current.timer = this.#animateCursor(cursor)
    } else {
      root.style.setProperty(
        "--cursor-preview-cursor",
        cursor.path ? cursorValue(cursor, cursor.path, "default") : "default",
      )
    }
  }

  // With an element, stops only a preview whose marker is inside it.
  static stop(within?: Element) {
    const current = this.#current
    if (!current || (within && !within.contains(current.marker))) return

    window.clearInterval(current.timer)
    current.marker.classList.remove(CURRENT_CLASS)
    this.#current = null

    const root = document.documentElement
    root.classList.remove(ACTIVE_CLASS)
    root.style.removeProperty("--cursor-preview-cursor")
    root.style.removeProperty("--cursor-preview-pointer")
  }

  // Stops the marker's own preview, or starts it in place of any other.
  static toggle(marker: Element, pair: CursorPreviewPair) {
    const wasCurrent = this.#current?.marker === marker
    this.stop()
    if (!wasCurrent) this.start(marker, pair)
  }

  // The frames are stamped from JS because the var feeding the page-wide rule
  // can be set but not keyframe-animated (Chrome won't animate custom properties).
  static #animateCursor(cursor: AnimatedCursorPreviewImage): number {
    for (const url of cursor.frames) new Image().src = url
    const { frames, frameMs } = cursorPlayback(cursor)
    const sequence = frames.map((url) => cursorValue(cursor, url, "default"))
    let index = 0
    const step = () => {
      document.documentElement.style.setProperty("--cursor-preview-cursor", sequence[index])
      index = (index + 1) % sequence.length
    }
    step()
    return window.setInterval(step, frameMs)
  }
}
